import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type ReferralCodeStatus = 'idle' | 'checking' | 'valid' | 'invalid';

export const useReferralCodeValidation = () => {
  const [status, setStatus] = useState<ReferralCodeStatus>('idle');
  const [message, setMessage] = useState<string>('');

  const validateReferralCode = async (code: string) => {
    if (!code || code.trim().length === 0) {
      setStatus('idle');
      setMessage('');
      return;
    }

    setStatus('checking');
    setMessage('Checking referral code...');

    try {
      // Only check referral_codes table for user referrals
      const { data: referralData, error: referralError } = await supabase
        .from('referral_codes')
        .select('id, is_used, expires_at, referrer_user_id')
        .eq('code', code.trim())
        .eq('is_used', false)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      if (referralError) {
        console.error('Error validating referral code:', referralError);
        setStatus('invalid');
        setMessage('Error validating referral code');
        return;
      }

      if (referralData) {
        setStatus('valid');
        setMessage('Valid referral code! You\'ll receive bonus credits.');
      } else {
        setStatus('invalid');
        setMessage('Invalid or expired referral code');
      }
    } catch (error) {
      console.error('Exception validating referral code:', error);
      setStatus('invalid');
      setMessage('Error validating referral code');
    }
  };

  // Debounced validation
  const debouncedValidate = (code: string) => {
    if (!code || code.trim().length === 0) {
      setStatus('idle');
      setMessage('');
      return () => {};
    }
    
    const timer = setTimeout(() => {
      validateReferralCode(code);
    }, 500);

    return () => clearTimeout(timer);
  };

  return {
    status,
    message,
    validateReferralCode: debouncedValidate
  };
};